import { type ApprovalWorkflowDetailsApiModel } from '@webapp/core/data-access/api-models/ApprovalWorkflowDetailsApiModel'
import { mapApprovalWorkflowDetailsApiModel } from '@webapp/core/data-access/mappers/mapApprovalWorkflowDetailsApiModel'
import { type ApprovalWorkflowDetailsConstructorData } from '@webapp/core/entities/internal/ApprovalWorkflowDetails/ApprovalWorkflowDetails'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type DenyOrdersData } from '@webapp/modules/purchasing/business-logic/types/DenyOrdersData'
import { type EditOrderDetailsData } from '@webapp/modules/purchasing/business-logic/types/EditOrderDetailsData'
import {
  type BudgetCategoryConstructorData,
  type IBudgetRepository,
  type LegacyBudgetConstructorData,
  mapBudgetCategoryApiModel,
  type LegacyBudgetOverviewConstructorData,
} from '@webapp/platform/budgets'
import {
  type IAccountRepository,
  type AccountConstructor,
} from '@webapp/platform/finance'
import { type IOrderRepository } from '../../../business-logic/interfaces/IOrderRepository'
import { type ApproveOrdersData } from '../../../business-logic/types/ApproveOrdersData'
import { type GetAreOrdersWithinBudgetData } from '../../../business-logic/types/GetAreOrdersWithinBudgetData'
import { type GetOrderDetailsData } from '../../../business-logic/types/GetOrderDetailsData'
import { type ListApprovableOrdersData } from '../../../business-logic/types/ListApprovableOrdersData'
import { type ListWatchlistApproversData } from '../../../business-logic/types/ListWatchlistApproversData'
import { type ApprovedOrderItemConstructor } from '../../../entities/ApprovedOrderItem'
import { type OrderApprovalCountsConstructor } from '../../../entities/OrderApprovalCounts'
import { type OrderItemsResponseApiModel } from '../../api-models/OrderItemsResponseApiModel'
import { mapApprovableOrderSummaryApiModel } from '../../mappers/mapApprovableOrderSummaryApiModel'
import { mapApprovedOrderSiblingItemApiModel } from '../../mappers/mapApprovedOrderSiblingItemApiModel'
import { mapOrderApiModel } from '../../mappers/mapOrderApiModel'
import { mapOrderApprovalCountsApiModel } from '../../mappers/mapOrderApprovalCountsApiModel'
import { mapWatchlistApproverApiModel } from '../../mappers/mapWatchlistApproverApiModel'
import { type ApprovableOrderSearchOperator } from '../../types/ApprovableOrderSearchOperator'
import { type ApproveOrdersPayload } from '../../types/ApproveOrdersPayload'
import { type DenyOrdersPayload } from '../../types/DenyOrdersPayload'
import { type ExportApprovalsPayload } from '../../types/ExportApprovalsPayload'
import { type ExportOrderApprovalsParams } from '../../types/ExportOrderApprovalsParams'
import { type GetApprovableOrderSearchOperatorsResponse } from '../../types/GetApprovableOrderSearchOperatorsResponse'
import { type GetAreOrdersWithinBudgetResponse } from '../../types/GetAreOrdersWithinBudgetResponse'
import { type GetBudgetCategoriesForOrderResponse } from '../../types/GetBudgetCategoriesForOrderResponse'
import { type GetOrderApprovalCountsParams } from '../../types/GetOrderApprovalCountsParams'
import { type GetOrderApprovalCountsResponse } from '../../types/GetOrderApprovalCountsResponse'
import { type GetOrderCommittingAmountsResponse } from '../../types/GetOrderCommittingAmountsResponse'
import { type GetOrderDetailsResponse } from '../../types/GetOrderDetailsResponse'
import { type GetSpendBySecondaryCategoryResponse } from '../../types/GetSpendBySecondaryCategoryResponse'
import { type ListApprovableOrdersParams } from '../../types/ListApprovableOrdersParams'
import { type ListApprovableOrdersResponse } from '../../types/ListApprovableOrdersResponse'
import { type ListOrderWatchlistApproversParams } from '../../types/ListOrderWatchlistApproversParams'
import { type ListOrderWatchlistApproversResponse } from '../../types/ListOrderWatchlistApproversResponse'
import { type PatchOrderPayload } from '../../types/PatchOrderPayload'
import { type PatchOrderResponse } from '../../types/PatchOrderResponse'
import { type PostOrderApprovalResponse } from '../../types/PostOrderApprovalResponse'
import { type PutEditOrderDetailsPayload } from '../../types/PutEditOrderDetailsPayload'

// Belt-and-braces fallbacks: usePagination resolves these from
// pagination.config.ts ('order-approval-list') before the repo is ever called.
// The sort default is the PUR_3723-off key, matching that config entry.
const DEFAULT_APPROVABLE_ORDER_SORT = '-num'
const DEFAULT_APPROVABLE_ORDER_PAGE_SIZE = 10

interface IDependency {
  httpClient: IHttpClient
  budgetRepository: IBudgetRepository
  accountRepository: IAccountRepository
}

export class OrderRepository implements IOrderRepository {
  constructor(private readonly deps: IDependency) {}

  async approveOrders(data: ApproveOrdersData): Promise<void> {
    try {
      const resp = await this.deps.httpClient.post<
        PostOrderApprovalResponse,
        ApproveOrdersPayload
      >('/api/v3/orders/approval/', {
        action: 'approve',
        skip_budget_overture_check: data.skipBudgetOvertureCheck || false,
        next_approvers: data.nextApprovers,
      })

      if (!resp.data.success) {
        // No hardcoded fallback copy — an empty message is what makes the
        // Presenter's localized `unexpectedErrorMessage` reach the user. The
        // `?? ''` is load-bearing: `/api/v3/orders/**` is outside OpenAPI
        // coverage, so `description: string` is an assumption, and a missing
        // one would otherwise surface as the literal "undefined" in the toast
        // (RepoError assigns `message` raw; getMessageFromError String()s it).
        throw new RepoError({
          message: resp.data.description ?? '',
          code: null,
          status: 400,
          response: null,
        })
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getOrderItems(
    orderId: number
  ): Promise<ApprovedOrderItemConstructor[]> {
    try {
      const res = await this.deps.httpClient.get<{
        data: OrderItemsResponseApiModel
      }>(`/api/v3/orders/${orderId}/`, {
        params: { format: 'json' },
      })

      const order = res.data.data

      return order.order_items.map((item) =>
        mapApprovedOrderSiblingItemApiModel(item, order.uuid)
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async deleteOrder(orderId: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(`/api/v3/orders/${orderId}/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async denyOrders(data: DenyOrdersData): Promise<void> {
    try {
      const resp = await this.deps.httpClient.post<
        PostOrderApprovalResponse,
        DenyOrdersPayload
      >('/api/v3/orders/approval/', {
        action: 'deny',
        denial_reasons: data.denialReasons,
      })

      if (!resp.data.success) {
        // See approveOrders — the localized fallback lives in the Presenter,
        // and `?? ''` guards a missing description.
        throw new RepoError({
          message: resp.data.description ?? '',
          code: null,
          status: 400,
          response: null,
        })
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async duplicateOrder(orderId: number): Promise<void> {
    try {
      await this.deps.httpClient.post(`/api/v3/order-cart/${orderId}/recreate/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async editOrderDetails(
    orderId: number,
    data: PutEditOrderDetailsPayload
  ): Promise<void> {
    try {
      await this.deps.httpClient.put<void, PutEditOrderDetailsPayload>(
        `/api/v3/orders/${orderId}/`,
        data
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getBudgetCategoriesForOrder(
    orderId: number
  ): Promise<
    RepoPaginatedResponse<
      BudgetCategoryConstructorData,
      { uncategorizedSpend: string }
    >
  > {
    try {
      const res =
        await this.deps.httpClient.post<GetBudgetCategoriesForOrderResponse>(
          '/api/v3/budget-categories/search/',
          {
            order_id: orderId,
          },
          {
            params: {
              matches_current_datetime: 'True',
            },
          }
        )

      const { data, metadata } = res.data

      return {
        data: data.map((budgetCategory) =>
          mapBudgetCategoryApiModel(budgetCategory)
        ),
        metadata: {
          uncategorizedSpend: metadata?.uncategorized_amount || '0',
        },
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getLegacyBudgets(
    accountCodeIds: number[],
    secondaryCategoryId: number
  ): Promise<RepoStandardResponse<LegacyBudgetConstructorData[]>> {
    return this.deps.budgetRepository.getLegacyBudgets(accountCodeIds, [
      secondaryCategoryId,
    ])
  }

  async getLegacyBudgetOverview(
    secondaryCategoryId: number
  ): Promise<RepoStandardResponse<LegacyBudgetOverviewConstructorData | null>> {
    return this.deps.budgetRepository.getLegacyBudgetOverview(
      secondaryCategoryId
    )
  }

  async getOrderCommittingAmounts(
    orderId: number
  ): Promise<RepoStandardResponse<{ [accountCodeId: string]: string }>> {
    try {
      const res =
        await this.deps.httpClient.get<GetOrderCommittingAmountsResponse>(
          `/api/v3/orders/${orderId}/committing-amounts/`
        )

      const { data } = res.data

      const committingAmountsByAccountCodeId = data.reduce(
        (acc, item) => {
          acc[item.account_code_id] = item.amount
          return acc
        },
        {} as Record<string, string>
      )

      return {
        data: committingAmountsByAccountCodeId,
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getSpendBySecondaryCategory(
    secondaryCategoryId: number,
    orderId: number,
    startDate: ISODateString,
    endDate: ISODateString
  ): Promise<
    RepoStandardResponse<{
      approved: string
      currentRequestPending: string
      invoiced: string
      pending: string
      purchased: string
      received: string
    }>
  > {
    try {
      const res =
        await this.deps.httpClient.get<GetSpendBySecondaryCategoryResponse>(
          '/api/v3/allocations-aggregate/',
          {
            params: {
              order_id: orderId,
              department_ids: secondaryCategoryId,
              start_date: startDate,
              end_date: endDate,
            },
          }
        )
      const { data } = res.data
      return {
        data: {
          approved: data.approved,
          currentRequestPending: data.request_pending_amount,
          invoiced: data.invoiced,
          pending: data.pending,
          purchased: data.purchased,
          received: data.received,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getDetails(uuid: uuid): Promise<GetOrderDetailsData> {
    try {
      const res = await this.deps.httpClient.get<GetOrderDetailsResponse>(
        `/api/v3/orders/${uuid}/`
      )

      const { data: order, metadata } = res.data

      return {
        data: mapOrderApiModel(order),
        metadata: {
          permissions: {
            canApprove: metadata?.permissions.can_approve || false,
            canDelete: metadata?.permissions.can_delete || false,
            canEdit: metadata?.permissions.can_edit || false,
            hasPendingItems: metadata?.permissions.has_pending_items || false,
          },
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getIsOrderWithinBudget(
    orderId: number
  ): Promise<RepoStandardResponse<boolean>> {
    try {
      const res = await this.deps.httpClient.get<RepoStandardResponse<boolean>>(
        `/api/order/?context=approval&check_within_budget=True&order_ids=${orderId}`
      )

      return {
        data: res.data.data,
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getAreOrdersWithinBudget(
    orderIds: number[]
  ): Promise<GetAreOrdersWithinBudgetData> {
    try {
      const res =
        await this.deps.httpClient.get<GetAreOrdersWithinBudgetResponse>(
          `/api/v3/orders/approval/budget_check/?order_ids=${orderIds.join(',')}`
        )

      return {
        data: res.data,
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async sendApprovalReminder(orderId: number, message: string): Promise<void> {
    try {
      await this.deps.httpClient.post<
        RepoStandardResponse,
        {
          message: Nullable<string>
        }
      >(`/api/v3/orders/${orderId}/remind-approver/`, {
        message: message || null,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async update(orderId: number, data: EditOrderDetailsData): Promise<void> {
    try {
      await this.deps.httpClient.patch<PatchOrderResponse, PatchOrderPayload>(
        `/api/v4/orders/${orderId}/`,
        {
          location: data.primaryCategoryId,
          department: data.secondaryCategoryId,
          date_required: data.dateRequired,
          description: data.description,
          punchout_shipping_address: data.punchoutShippingAddressId,
          order_items: data.orderItems
            ? data.orderItems.map((item) => ({
                id: item.id,
                account: item.accountId,
              }))
            : [],
        }
      )

      return
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  getAccountsByAccountCodeIds(
    secondaryCategoryId: number,
    accountCodeIds: number[],
    requesterId: number,
    pageSize?: number
  ): Promise<RepoPaginatedResponse<AccountConstructor>> {
    return this.deps.accountRepository.getRequestableAccounts(
      secondaryCategoryId,
      {
        account_code: accountCodeIds.join(','),
        requester: requesterId,
        pageSize: pageSize ?? 9999,
      }
    )
  }

  async getApprovalWorkflowDetails(
    uuid: uuid
  ): Promise<RepoStandardResponse<ApprovalWorkflowDetailsConstructorData>> {
    try {
      const res =
        await this.deps.httpClient.get<ApprovalWorkflowDetailsApiModel>(
          `/api/v3/orders/${uuid}/approval_workflow/`
        )

      return {
        data: mapApprovalWorkflowDetailsApiModel(res.data),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async listApprovableOrders(
    params: ListApprovableOrdersParams
  ): Promise<ListApprovableOrdersData> {
    try {
      const res = await this.deps.httpClient.get<ListApprovableOrdersResponse>(
        `/api/v2/orders/approvable/${params.status}/`,
        {
          params: {
            format: 'json',
            counts: false,
            simple_orders: true,
            order_by: params.orderBy || DEFAULT_APPROVABLE_ORDER_SORT,
            page: params.page || 1,
            page_size: params.pageSize || DEFAULT_APPROVABLE_ORDER_PAGE_SIZE,
            search: params.search,
            subscribed: params.subscribed,
            location: params.location,
            department: params.department,
            is_punchout: params.isPunchout,
          },
        }
      )

      const { data, metadata } = res.data

      return {
        orders: data.map(mapApprovableOrderSummaryApiModel),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 1,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getApprovalCounts(
    params: GetOrderApprovalCountsParams
  ): Promise<OrderApprovalCountsConstructor> {
    try {
      const res =
        await this.deps.httpClient.get<GetOrderApprovalCountsResponse>(
          '/api/v3/orders/count/',
          {
            params: {
              format: 'json',
              type: 'OrderApproval',
              search: params.search,
              location: params.location,
              department: params.department,
              subscribed: params.subscribed,
            },
          }
        )

      return mapOrderApprovalCountsApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async listWatchlistApprovers(
    params: ListOrderWatchlistApproversParams
  ): Promise<ListWatchlistApproversData> {
    try {
      const res =
        await this.deps.httpClient.get<ListOrderWatchlistApproversResponse>(
          '/api/v2/settings/approval-subscribed/approver/count/',
          {
            params: {
              object_type: 'order',
              order_by: 'approver__firstName',
              page: params.page || 1,
              page_size: params.pageSize || 10,
              search: params.search,
              approver_id: params.approverId,
            },
          }
        )

      const { data, metadata } = res.data

      return {
        data: data.map(mapWatchlistApproverApiModel),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 1,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getApprovableOrderSearchOperators(): Promise<
    ApprovableOrderSearchOperator[]
  > {
    try {
      const res =
        await this.deps.httpClient.options<GetApprovableOrderSearchOperatorsResponse>(
          '/api/v2/orders/approvable/pending/'
        )

      return res.data.data.search_operators
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async exportApprovals(params: ExportOrderApprovalsParams): Promise<void> {
    try {
      await this.deps.httpClient.post<void, ExportApprovalsPayload>(
        '/api/v3/orders/export/',
        {
          export_email: true,
          type: 'approvals',
          status: params.status,
          subscribed: params.subscribed,
        }
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
